/**
 * usePlannerData Hook
 *
 * Fetches demand forecasts, shadow batches, and real batches organised
 * by week and strain family for the Planner approval view.
 *
 * Returns weekly data showing demand vs supply gaps per family,
 * with shadow batches awaiting approval and real batches in production.
 *
 * US-C2-005: Planner Data Layer
 * Strain Families: groups demand by family (species), not individual strains
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/supabase-client'
import { createDate, toLocalDateString } from '@/utils/dateUtils'

/**
 * Get the Monday of the week containing a date
 * @param {string} dateStr - Date string (YYYY-MM-DD)
 * @returns {string} Week start date (YYYY-MM-DD)
 */
function getWeekStart(dateStr) {
  const date = new Date(dateStr + 'T00:00:00')
  const day = date.getDay()
  const diff = (day === 0 ? -6 : 1) - day
  date.setDate(date.getDate() + diff)
  return toLocalDateString(date)
}

/**
 * Hook for planner data: demand vs supply by week and strain family
 *
 * @param {Object} params - Query parameters
 * @param {number} [params.weekCount=8] - How many weeks to look ahead
 * @returns {Object} {
 *   weekData: Array<{ weekStart, families: Array<{ familyId, familyName, familyCode, defaultStrainId, demandKg, shadowBatches, realBatches, supplyKg, gapKg, memberStrains }> }>,
 *   loading: boolean,
 *   error: string|null,
 *   refetch: Function
 * }
 */
export function usePlannerData({ weekCount = 8 } = {}) {
  const [weekData, setWeekData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  /**
   * Calculate date range for queries
   */
  const dateRange = useMemo(() => {
    const start = createDate()
    // Start from the Monday of the current week
    const day = start.getDay()
    const diff = (day === 0 ? -6 : 1) - day
    start.setDate(start.getDate() + diff)

    const end = createDate()
    end.setDate(start.getDate() + (weekCount * 7))

    return {
      startDate: toLocalDateString(start),
      endDate: toLocalDateString(end)
    }
  }, [weekCount])

  /**
   * Build a lookup map of strain families with their member strains
   */
  const fetchFamilyLookup = useCallback(async () => {
    // Fetch families
    const { data: families, error: famErr } = await supabase
      .from('strain_families')
      .select('id, name, code, default_strain_id, display_order, active')
      .eq('active', true)
      .order('display_order', { ascending: true })

    if (famErr) throw famErr

    // Fetch active strains with family_id
    const { data: strains, error: strErr } = await supabase
      .from('strains')
      .select('id, name, code, family_id, active')
      .eq('active', true)

    if (strErr) throw strErr

    // Build lookup: familyId → family record (with member strains)
    const lookup = {}
    for (const f of families || []) {
      lookup[f.id] = {
        ...f,
        memberStrains: []
      }
    }

    // Attach strains to their families
    for (const s of strains || []) {
      if (s.family_id && lookup[s.family_id]) {
        lookup[s.family_id].memberStrains.push(s)
      }
    }

    return lookup
  }, [])

  /**
   * Fetch demand forecasts from recurring orders and AI suggestions,
   * grouped by strain family
   */
  const fetchDemand = useCallback(async (familyLookup) => {
    const demandByWeekFamily = {}

    // Fetch recurring order instances (pending, within date range)
    const { data: recurringData, error: recurringError } = await supabase
      .from('recurring_order_instances')
      .select(`
        id,
        scheduled_date,
        status,
        recurring_order:recurring_orders (
          id,
          recurring_order_items (
            quantity,
            product_id,
            products (
              strain,
              strain_family_id
            )
          )
        )
      `)
      .eq('status', 'pending')
      .gte('scheduled_date', dateRange.startDate)
      .lte('scheduled_date', dateRange.endDate)

    if (recurringError) throw recurringError

    for (const instance of recurringData || []) {
      const weekStart = getWeekStart(instance.scheduled_date)

      for (const item of instance.recurring_order?.recurring_order_items || []) {
        const familyId = item.products?.strain_family_id
        const family = familyId ? familyLookup[familyId] : null
        if (!family) continue

        const key = `${weekStart}:${family.id}`
        if (!demandByWeekFamily[key]) {
          demandByWeekFamily[key] = {
            weekStart,
            familyId: family.id,
            familyName: family.name,
            familyCode: family.code,
            defaultStrainId: family.default_strain_id,
            memberStrains: family.memberStrains,
            demandKg: 0
          }
        }
        demandByWeekFamily[key].demandKg += parseFloat(item.quantity || 0)
      }
    }

    // Fetch AI suggestions
    const { data: aiData, error: aiError } = await supabase
      .from('sale_suggestions')
      .select(`
        id,
        suggested_date,
        suggested_quantity,
        product:products (
          strain,
          strain_family_id
        )
      `)
      .eq('status', 'pending')
      .gte('suggested_date', dateRange.startDate)
      .lte('suggested_date', dateRange.endDate)

    if (aiError) throw aiError

    for (const suggestion of aiData || []) {
      const weekStart = getWeekStart(suggestion.suggested_date)
      const familyId = suggestion.product?.strain_family_id
      const family = familyId ? familyLookup[familyId] : null
      if (!family) continue

      const key = `${weekStart}:${family.id}`
      if (!demandByWeekFamily[key]) {
        demandByWeekFamily[key] = {
          weekStart,
          familyId: family.id,
          familyName: family.name,
          familyCode: family.code,
          defaultStrainId: family.default_strain_id,
          memberStrains: family.memberStrains,
          demandKg: 0
        }
      }
      demandByWeekFamily[key].demandKg += parseFloat(suggestion.suggested_quantity || 0)
    }

    return demandByWeekFamily
  }, [dateRange])

  /**
   * Fetch shadow batches (pending approval)
   */
  const fetchShadowBatches = useCallback(async () => {
    const { data, error: fetchError } = await supabase
      .from('production_batches')
      .select(`
        id,
        batch_code,
        strain_id,
        total_blocks,
        current_state,
        is_shadow,
        approval_status,
        target_kg_fixed,
        target_kg_confidence,
        demand_source_kg,
        demand_confidence_tier,
        demand_week_start,
        inoculation_date,
        spawn_bags_required,
        manually_adjusted,
        strains!inner (
          id,
          name,
          code,
          family_id,
          active
        )
      `)
      .eq('is_shadow', true)
      .eq('strains.active', true)
      .in('approval_status', ['pending', 'rejected'])
      .gte('demand_week_start', dateRange.startDate)
      .lte('demand_week_start', dateRange.endDate)
      .order('demand_week_start', { ascending: true })

    if (fetchError) throw fetchError
    return data || []
  }, [dateRange])

  /**
   * Fetch real batches (approved / active)
   */
  const fetchRealBatches = useCallback(async () => {
    const { data, error: fetchError } = await supabase
      .from('production_batches')
      .select(`
        id,
        batch_code,
        strain_id,
        total_blocks,
        current_state,
        is_shadow,
        approval_status,
        target_kg_fixed,
        total_harvest_kg,
        demand_week_start,
        inoculation_date,
        first_harvest_date,
        strains!inner (
          id,
          name,
          code,
          family_id,
          active
        )
      `)
      .eq('is_shadow', false)
      .eq('strains.active', true)
      .gte('demand_week_start', dateRange.startDate)
      .lte('demand_week_start', dateRange.endDate)
      .order('demand_week_start', { ascending: true })

    if (fetchError) throw fetchError
    return data || []
  }, [dateRange])

  /**
   * Assemble week data from demand, shadow batches, and real batches.
   * Groups by strain family instead of individual strains.
   */
  const assembleWeekData = useCallback((demandByWeekFamily, shadowBatches, realBatches, familyLookup) => {
    // Build a map: weekStart → familyId → { demand, shadow, real }
    const weekFamilyMap = {}

    // Seed from demand data
    for (const entry of Object.values(demandByWeekFamily)) {
      const { weekStart, familyId, familyName, familyCode, defaultStrainId, memberStrains, demandKg } = entry
      if (!weekFamilyMap[weekStart]) weekFamilyMap[weekStart] = {}
      if (!weekFamilyMap[weekStart][familyId]) {
        weekFamilyMap[weekStart][familyId] = {
          familyId,
          familyName,
          familyCode,
          defaultStrainId,
          memberStrains,
          demandKg: 0,
          shadowBatches: [],
          realBatches: [],
          supplyKg: 0
        }
      }
      weekFamilyMap[weekStart][familyId].demandKg += demandKg
    }

    // Add shadow batches — group by strain's family
    for (const batch of shadowBatches) {
      const weekStart = batch.demand_week_start
      if (!weekStart) continue
      const familyId = batch.strains?.family_id
      if (!familyId) continue

      if (!weekFamilyMap[weekStart]) weekFamilyMap[weekStart] = {}
      if (!weekFamilyMap[weekStart][familyId]) {
        const family = familyLookup[familyId]
        weekFamilyMap[weekStart][familyId] = {
          familyId,
          familyName: family?.name ?? 'Unknown',
          familyCode: family?.code ?? '??',
          defaultStrainId: family?.default_strain_id ?? null,
          memberStrains: family?.memberStrains ?? [],
          demandKg: 0,
          shadowBatches: [],
          realBatches: [],
          supplyKg: 0
        }
      }
      weekFamilyMap[weekStart][familyId].shadowBatches.push(batch)
    }

    // Add real batches and calculate supply — group by strain's family
    for (const batch of realBatches) {
      const weekStart = batch.demand_week_start
      if (!weekStart) continue
      const familyId = batch.strains?.family_id
      if (!familyId) continue

      if (!weekFamilyMap[weekStart]) weekFamilyMap[weekStart] = {}
      if (!weekFamilyMap[weekStart][familyId]) {
        const family = familyLookup[familyId]
        weekFamilyMap[weekStart][familyId] = {
          familyId,
          familyName: family?.name ?? 'Unknown',
          familyCode: family?.code ?? '??',
          defaultStrainId: family?.default_strain_id ?? null,
          memberStrains: family?.memberStrains ?? [],
          demandKg: 0,
          shadowBatches: [],
          realBatches: [],
          supplyKg: 0
        }
      }
      weekFamilyMap[weekStart][familyId].realBatches.push(batch)

      const estimatedYield = batch.target_kg_fixed
        ? parseFloat(batch.target_kg_fixed)
        : (batch.total_blocks || 0) * 0.3
      weekFamilyMap[weekStart][familyId].supplyKg += estimatedYield
    }

    // Convert map to sorted array of week objects
    const weeks = Object.keys(weekFamilyMap).sort().map(weekStart => {
      const families = Object.values(weekFamilyMap[weekStart]).map(family => ({
        ...family,
        gapKg: family.demandKg - family.supplyKg
      }))

      // Sort families by name for consistent display
      families.sort((a, b) => a.familyName.localeCompare(b.familyName))

      return {
        weekStart,
        families
      }
    })

    return weeks
  }, [])

  /**
   * Regenerate shadow batches for all weeks in the date range.
   * Calls the DB function that creates/updates shadow batches from demand.
   */
  const regenerateShadowBatches = useCallback(async () => {
    const weeks = []
    const start = new Date(dateRange.startDate + 'T00:00:00')
    const end = new Date(dateRange.endDate + 'T00:00:00')
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 7)) {
      weeks.push(toLocalDateString(d))
    }

    // Fire all weeks in parallel — each call is idempotent
    await Promise.allSettled(
      weeks.map(weekStart =>
        supabase.rpc('regenerate_shadow_block_batches_for_week', {
          p_harvest_week: weekStart
        })
      )
    )
  }, [dateRange])

  /**
   * Main fetch: load all data and assemble
   */
  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Regenerate shadow batches from current demand before fetching
      await regenerateShadowBatches()

      // Fetch family lookup first — needed by both demand and assembly
      const familyLookup = await fetchFamilyLookup()

      const [demandByWeekFamily, shadowBatches, realBatches] = await Promise.all([
        fetchDemand(familyLookup),
        fetchShadowBatches(),
        fetchRealBatches()
      ])

      const assembled = assembleWeekData(demandByWeekFamily, shadowBatches, realBatches, familyLookup)
      setWeekData(assembled)
    } catch (err) {
      console.error('[usePlannerData] Error fetching:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [regenerateShadowBatches, fetchFamilyLookup, fetchDemand, fetchShadowBatches, fetchRealBatches, assembleWeekData])

  // Initial fetch
  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  return {
    weekData,
    loading,
    error,
    refetch: fetchAll
  }
}

export default usePlannerData
